import { useEffect, useRef, useState } from 'react';
import { nodeTypeConfig, useFlowBuilderStore } from '../../stores/flowBuilderStore';
import { Maximize2, Minimize2, X, Trash2, MessageSquare } from 'lucide-react';
import { iconMap, getIconName } from './CustomNode';
import type { NodeType } from '../../types';
import {
  AiAnalyzeImageConfig,
  AiResponseConfig,
  AiTranscribeAudioConfig,
  BusinessHoursConfig,
  ConditionConfig,
  CreateCompanyConfig,
  DelayConfig,
  EndSessionConfig,
  ExtractDataConfig,
  GetCrmEntityConfig,
  GetEmployeeDataConfig,
  HttpConfig,
  MarketingCaptureConfig,
  MessageTypeConfig,
  MoveDealStageConfig,
  ParseJsonConfig,
  SearchContactConfig,
  SendButtonsConfig,
  SendCarouselConfig,
  SendFileConfig,
  SendImageConfig,
  SendMessageConfig,
  SetVariableConfig,
  TransferToOperatorConfig,
  TransferToQueueConfig,
  UpdateCrmConfig,
  WaitChoiceConfig,
  WaitInputConfig,
  WhisperMessageConfig,
} from './nodes-config';
import { VariablePicker } from './VariablePicker';

export function NodeConfigPanel() {
  const { selectedNode, updateNodeConfig, updateNodeLabel, closePanel, deleteNode } = useFlowBuilderStore();
  const panelBodyRef = useRef<HTMLDivElement | null>(null);
  const lastFocusedFieldRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
  const [nodeLabelInput, setNodeLabelInput] = useState<string>('');
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [insertHint, setInsertHint] = useState<string>('');
  const [hasInsertableTextField, setHasInsertableTextField] = useState<boolean>(false);
  const snapshotRef = useRef<{ id: string; label: string; config: Record<string, any> } | null>(null);

  if (!selectedNode) return null;

  const nodeData = selectedNode.data as {
    type: NodeType;
    label: string;
    color: string;
    config: Record<string, any>;
  };

  const handleConfigChange = (key: string, value: any) => {
    updateNodeConfig(selectedNode.id, {
      ...nodeData.config,
      [key]: value,
    });
  };

  const handleBatchConfigChange = (updates: Record<string, any>) => {
    updateNodeConfig(selectedNode.id, {
      ...nodeData.config,
      ...updates,
    });
  };

  const getDefaultNodeLabel = () => {
    const defaultLabel = nodeTypeConfig[nodeData.type]?.label;
    return String(defaultLabel || nodeData.type || 'Nó').trim();
  };

  const commitNodeLabel = (rawLabel: string) => {
    const trimmed = String(rawLabel || '').trim();
    const resolvedLabel = trimmed.length > 0 ? trimmed : getDefaultNodeLabel();
    setNodeLabelInput(resolvedLabel);
    if (resolvedLabel !== String(nodeData.label || '')) {
      updateNodeLabel(selectedNode.id, resolvedLabel);
    }
  };

  const isTextField = (element: Element | null): element is HTMLInputElement | HTMLTextAreaElement => {
    if (!element) {
      return false;
    }
    return element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement;
  };

  const isValidInsertionTarget = (
    element: HTMLInputElement | HTMLTextAreaElement | null,
  ): element is HTMLInputElement | HTMLTextAreaElement => {
    const panelRoot = panelBodyRef.current;
    if (!element || !panelRoot) {
      return false;
    }
    if (!element.isConnected || element.disabled || element.readOnly) {
      return false;
    }
    if (!panelRoot.contains(element)) {
      return false;
    }
    if (element.closest('[data-variable-picker-root]')) {
      return false;
    }
    if ((element as HTMLElement).dataset.labelInput) {
      return false;
    }
    return true;
  };

  useEffect(() => {
    lastFocusedFieldRef.current = null;
    setInsertHint('');
    setHasInsertableTextField(false);

    const panelRoot = panelBodyRef.current;
    if (!panelRoot) {
      return;
    }

    const onFocusIn = (event: FocusEvent) => {
      const target = event.target as Element | null;
      if (!isTextField(target)) {
        return;
      }
      if (!isValidInsertionTarget(target)) {
        return;
      }
      lastFocusedFieldRef.current = target;
      setInsertHint('');
    };

    panelRoot.addEventListener('focusin', onFocusIn);
    return () => {
      panelRoot.removeEventListener('focusin', onFocusIn);
    };
  }, [selectedNode.id]);

  useEffect(() => {
    setNodeLabelInput(String(nodeData.label || ''));
  }, [selectedNode.id, nodeData.label]);

  useEffect(() => {
    setIsExpanded(false);
  }, [selectedNode.id]);

  useEffect(() => {
    const panelRoot = panelBodyRef.current;
    if (!panelRoot) {
      setHasInsertableTextField(false);
      return;
    }

    const hasValidField = Array.from(panelRoot.querySelectorAll('input, textarea')).some((element) => {
      if (!isTextField(element)) {
        return false;
      }
      if (element instanceof HTMLInputElement) {
        const inputType = (element.type || 'text').toLowerCase();
        if (['checkbox', 'radio', 'file', 'button', 'submit', 'reset', 'range', 'color'].includes(inputType)) {
          return false;
        }
      }
      return isValidInsertionTarget(element);
    });

    setHasInsertableTextField(hasValidField);
  }, [selectedNode.id, nodeData.type, nodeData.config]);

  const handleInsertVariableInFocusedField = (variable: string) => {
    const active = document.activeElement as Element | null;
    const activeField = isTextField(active) ? active : null;
    const targetField = isValidInsertionTarget(activeField)
      ? activeField
      : isValidInsertionTarget(lastFocusedFieldRef.current)
        ? lastFocusedFieldRef.current
        : null;

    if (!targetField) {
      setInsertHint('Selecione primeiro um campo de texto deste nó.');
      return;
    }

    const currentValue = targetField.value || '';
    const start = Number.isFinite(targetField.selectionStart as number)
      ? Number(targetField.selectionStart)
      : currentValue.length;
    const end = Number.isFinite(targetField.selectionEnd as number)
      ? Number(targetField.selectionEnd)
      : currentValue.length;

    const nextValue = currentValue.slice(0, start) + variable + currentValue.slice(end);

    const prototype = Object.getPrototypeOf(targetField) as HTMLInputElement | HTMLTextAreaElement;
    const descriptor = Object.getOwnPropertyDescriptor(prototype, 'value');
    if (descriptor?.set) {
      descriptor.set.call(targetField, nextValue);
    } else {
      targetField.value = nextValue;
    }

    targetField.dispatchEvent(new Event('input', { bubbles: true }));
    targetField.dispatchEvent(new Event('change', { bubbles: true }));

    const caret = start + variable.length;
    try {
      targetField.focus();
      targetField.setSelectionRange(caret, caret);
    } catch {
      // ignore selection errors on unsupported input types
    }

    lastFocusedFieldRef.current = targetField;
    setInsertHint('');
  };

  const variableSelectorBlock = (
    <div
      data-variable-picker-root
      className="mt-5 rounded-lg border border-blue-200 dark:border-blue-500/30 bg-blue-50 dark:bg-blue-500/10 p-3"
    >
      <p className="text-xs text-blue-700 dark:text-blue-200">
        Clique no campo desejado e selecione a variável:
      </p>
      <div className="mt-2">
        <VariablePicker onInsert={handleInsertVariableInFocusedField} />
      </div>
      <p className="mt-1 text-[11px] text-blue-700/90 dark:text-blue-200/90">
        A variável será inserida no último campo selecionado.
      </p>
      {insertHint && (
        <p className="mt-1 text-[11px] text-amber-600 dark:text-amber-300">{insertHint}</p>
      )}
    </div>
  );

  const nodeLabelFieldCompact = (
    <div className="mt-2.5">
      <label className="mb-1 block text-[10.5px] font-medium uppercase tracking-[0.08em] text-slate-400">
        Nome do passo
      </label>
      <input
        data-label-input="true"
        value={nodeLabelInput}
        onChange={(event) => {
          const next = event.target.value;
          setNodeLabelInput(next);
          updateNodeLabel(selectedNode.id, next);
        }}
        onBlur={() => commitNodeLabel(nodeLabelInput)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            commitNodeLabel(nodeLabelInput);
          }
        }}
        placeholder={getDefaultNodeLabel()}
        className="h-8 w-full rounded-md border border-slate-700 bg-slate-950 px-2.5 text-[12.5px] text-slate-100 placeholder:text-slate-500 focus:border-violet-500 focus:outline-none"
      />
    </div>
  );

  // Snapshot the step when it opens so "Cancelar" can revert live edits (mockup behaviour)
  useEffect(() => {
    snapshotRef.current = {
      id: selectedNode.id,
      label: nodeData.label,
      config: JSON.parse(JSON.stringify(nodeData.config || {})),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedNode.id]);

  // Close modal on Escape key
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closePanel();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [closePanel]);

  const renderConfigFields = () => {
    const commonProps = {
      config: nodeData.config,
      onChange: handleConfigChange,
    };

    switch (nodeData.type) {
      case 'SEARCH_CONTACT':
        return <SearchContactConfig {...commonProps} onBatchChange={handleBatchConfigChange} />;

      case 'GET_EMPLOYEE_DATA':
        return <GetEmployeeDataConfig {...commonProps} />;

      case 'EXTRACT_DATA':
        return <ExtractDataConfig {...commonProps} />;

      case 'CREATE_COMPANY':
      case 'CREATE_CONTACT':
      case 'CREATE_DEAL':
      case 'CREATE_LEAD':
        return <CreateCompanyConfig {...commonProps} nodeType={nodeData.type} />;

      case 'GET_CRM_ENTITY':
        return <GetCrmEntityConfig {...commonProps} />;

      case 'UPDATE_CONTACT': {
        const mode = String(nodeData.config?.mode || '').trim().toLowerCase();
        if (mode === 'get_crm_entity') {
          return <GetCrmEntityConfig {...commonProps} />;
        }
        if (mode === 'move_deal_stage') {
          return <MoveDealStageConfig {...commonProps} />;
        }
        return <UpdateCrmConfig {...commonProps} />;
      }

      case 'MOVE_DEAL_STAGE':
        return <MoveDealStageConfig {...commonProps} />;
      
      case 'SEND_MESSAGE':
        return <SendMessageConfig {...commonProps} />;

      case 'WHISPER_MESSAGE':
        return <WhisperMessageConfig {...commonProps} />;

      case 'SEND_FILE':
        return <SendFileConfig {...commonProps} />;

      case 'SEND_IMAGE':
        return <SendImageConfig {...commonProps} />;

      case 'SEND_CAROUSEL':
        return <SendCarouselConfig {...commonProps} />;

      case 'SEND_BUTTONS':
        return <SendButtonsConfig {...commonProps} />;

      case 'WAIT_INPUT':
        return <WaitInputConfig {...commonProps} />;

      case 'CONDITION':
        return <ConditionConfig {...commonProps} />;

      case 'MESSAGE_TYPE':
        return <MessageTypeConfig />;

      case 'BUSINESS_HOURS':
        return <BusinessHoursConfig {...commonProps} />;

      case 'WAIT_CHOICE':
        return <WaitChoiceConfig {...commonProps} />;

      case 'DELAY':
        return <DelayConfig {...commonProps} />;

      case 'AI_RESPONSE':
        return <AiResponseConfig {...commonProps} onBatchChange={handleBatchConfigChange} />;

      case 'AI_TRANSCRIBE_AUDIO':
        return <AiTranscribeAudioConfig {...commonProps} onBatchChange={handleBatchConfigChange} />;

      case 'AI_ANALYZE_IMAGE':
        return <AiAnalyzeImageConfig {...commonProps} onBatchChange={handleBatchConfigChange} />;

      case 'HTTP_REQUEST':
        return <HttpConfig {...commonProps} onBatchChange={handleBatchConfigChange} />;

      case 'TRANSFER_TO_QUEUE':
        return <TransferToQueueConfig {...commonProps} />;

      case 'TRANSFER_TO_OPERATOR':
        return <TransferToOperatorConfig {...commonProps} />;

      case 'END_SESSION':
        return <EndSessionConfig {...commonProps} />;

      case 'SET_VARIABLE':
        return <SetVariableConfig {...commonProps} />;

      case 'PARSE_JSON':
        return <ParseJsonConfig {...commonProps} />;

      case 'ADD_TAG':
        return <MarketingCaptureConfig {...commonProps} />;

      default:
        return (
          <p className="text-sm text-slate-500 dark:text-slate-300">
            Configuração não disponível para este tipo de nó.
          </p>
        );
    }
  };

  const HeaderIcon = iconMap[getIconName(nodeData.type)] || MessageSquare;
  const typeLabel = nodeTypeConfig[nodeData.type]?.label ?? nodeData.type;
  const canDelete = nodeData.type !== 'START';
  const subtitle =
    nodeData.type === 'AI_RESPONSE'
      ? 'Configure a inteligência artificial'
      : nodeData.type === 'HTTP_REQUEST'
      ? 'Configure a requisição HTTP'
      : 'Configure o passo';

  const handleCancel = () => {
    const snap = snapshotRef.current;
    if (snap && snap.id === selectedNode.id) {
      updateNodeConfig(selectedNode.id, snap.config);
      updateNodeLabel(selectedNode.id, snap.label);
    }
    closePanel();
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-6">
      <div className="absolute inset-0 bg-black/55 backdrop-blur-[2px]" onClick={closePanel} />
      <div
        className={`relative flex flex-col rounded-xl border border-slate-700/70 bg-slate-900 shadow-[0_24px_70px_rgba(0,0,0,0.65)] animate-in fade-in zoom-in-95 duration-200 ${
          isExpanded ? 'h-[92vh] w-[96vw] max-w-none' : 'max-h-[calc(100vh-48px)] w-[92vw] max-w-[640px]'
        }`}
      >
        <div className="flex items-start gap-3 border-b border-violet-500/25 px-4 pb-3 pt-3.5">
          <span className="mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-[7px] border border-white/5 bg-slate-800">
            <HeaderIcon className="h-[15px] w-[15px]" style={{ color: nodeData.color }} />
          </span>
          <div className="min-w-0 flex-1">
            <h3 className="text-[16px] font-medium leading-tight text-slate-100">{typeLabel}</h3>
            <p className="text-[11.5px] text-slate-400">{subtitle}</p>
            {nodeLabelFieldCompact}
          </div>
          <div className="flex items-center gap-0.5">
            {canDelete && (
              <button
                type="button"
                onClick={() => deleteNode(selectedNode.id)}
                className="grid h-[30px] w-[30px] place-items-center rounded-lg text-slate-400 transition-colors hover:bg-red-500/10 hover:text-red-300"
                title="Excluir passo"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            )}
            <button
              type="button"
              onClick={() => setIsExpanded((prev) => !prev)}
              className="grid h-[30px] w-[30px] place-items-center rounded-lg text-slate-400 transition-colors hover:bg-slate-800 hover:text-slate-200"
              title={isExpanded ? 'Reduzir painel' : 'Expandir painel'}
            >
              {isExpanded ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
            </button>
            <button
              type="button"
              onClick={closePanel}
              className="grid h-[30px] w-[30px] place-items-center rounded-lg text-slate-400 transition-colors hover:bg-slate-800 hover:text-slate-200"
              title="Fechar"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div
          ref={panelBodyRef}
          className={`flow-node-config flex-1 overflow-y-auto text-slate-200 ${isExpanded ? 'p-6' : 'p-4'}`}
        >
          {renderConfigFields()}
          {hasInsertableTextField ? variableSelectorBlock : null}
        </div>

        <div className="flex items-center justify-between gap-3 border-t border-slate-700/70 px-4 py-3">
          <p className="text-[11px] text-slate-500">Alterações aplicam ao vivo · Cancelar desfaz as deste passo.</p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleCancel}
              className="h-[34px] rounded-lg border border-slate-600 px-3.5 text-[13px] font-medium text-slate-200 transition-colors hover:bg-slate-800"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={closePanel}
              className="h-[34px] rounded-lg bg-violet-600 px-4 text-[13px] font-medium text-white transition-colors hover:bg-violet-500"
            >
              Salvar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
